import { ArmsRaceLastHit, ArmsRacePlayer, ArmsRaceState } from './traits';
import type { World } from 'koota';
import { fnv1a, hashToHex, quantize } from '../core/hash';
import { getContext } from './context';
import { matchWinScores, restoreMatchWins } from './rules/scores';
import {
  Aim,
  Combat,
  Controller,
  Health,
  Hazard,
  HazardKind,
  KillStreak,
  NetId,
  PartyPlayer,
  PartyState,
  Player,
  Projectile,
  ProjectileKind,
  Transform,
  Weapon,
} from './traits';

export type TraitSnapshot = {
  netId: number;
  traits: Record<string, Record<string, number | boolean | string>>;
};

export type WorldSnapshot = {
  tick: number;
  rng: number;
  nextId: number;
  entities: TraitSnapshot[];
  party?: Record<string, number | boolean>;
  matchWins?: number[];
  armsRace?: { winner: number };
};

export function serializeWorld(world: World): WorldSnapshot {
  const ctx = getContext(world);
  const entities: TraitSnapshot[] = [];
  world.query(NetId).updateEach(([net], entity) => {
    const snap: TraitSnapshot = { netId: net.id, traits: {} };
    const t = entity.get(Transform);
    if (t) snap.traits.Transform = { x: t.x, y: t.y, angle: t.angle };
    const h = entity.get(Health);
    if (h) snap.traits.Health = { hp: h.hp, maxHp: h.maxHp };
    const hazard = entity.get(Hazard);
    if (hazard?.kind === HazardKind.Cannon) snap.traits.Hazard = { ...hazard };
    const streak = entity.get(KillStreak);
    if (streak) snap.traits.KillStreak = { ...streak };
    const p = entity.get(Player);
    if (p) snap.traits.Player = { slot: p.slot, color: p.color, inputIndex: p.inputIndex };
    const c = entity.get(Controller);
    if (c) snap.traits.Controller = { grounded: c.grounded, facing: c.facing, vx: c.vx, vy: c.vy };
    const a = entity.get(Aim);
    if (a) snap.traits.Aim = { x: a.x, y: a.y };
    const w = entity.get(Weapon);
    if (w) snap.traits.Weapon = { defId: w.defId, ammo: w.ammo, thrown: w.thrown };
    const pr = entity.get(Projectile);
    if (pr) snap.traits.Projectile = { x: pr.x, y: pr.y, vx: pr.vx, vy: pr.vy, kind: pr.kind };
    if (pr && (pr.kind === ProjectileKind.ReturnDisc || pr.kind === ProjectileKind.Siphon))
      snap.traits.Projectile = { ...pr };
    if (pr && ctx.settings.mode === 'arms-race')
      snap.traits.Projectile = {
        ...snap.traits.Projectile,
        defId: pr.defId,
        creditStage: pr.creditStage,
      };
    const race = entity.get(ArmsRacePlayer);
    if (race) snap.traits.ArmsRacePlayer = { ...race };
    const lastHit = entity.get(ArmsRaceLastHit);
    if (lastHit) snap.traits.ArmsRaceLastHit = { ...lastHit };
    const partyPlayer = entity.get(PartyPlayer);
    if (partyPlayer) snap.traits.PartyPlayer = { ...partyPlayer };
    const cb = entity.get(Combat);
    if (cb) snap.traits.Combat = { blockMeter: cb.blockMeter, blocking: cb.blocking };
    entities.push(snap);
  });
  entities.sort((a, b) => a.netId - b.netId);
  return {
    tick: ctx.tick,
    rng: ctx.rng.getState(),
    nextId: ctx.ids.peek(),
    entities,
    party: { ...world.get(PartyState)! },
    matchWins: matchWinScores(world),
    ...(world.get(ArmsRaceState) ? { armsRace: { ...world.get(ArmsRaceState)! } } : {}),
  };
}

export function restoreWorld(world: World, snap: WorldSnapshot): void {
  const ctx = getContext(world);
  ctx.rng.setState(snap.rng);
  ctx.ids.setNext(snap.nextId);
  ctx.tick = snap.tick;
  if (snap.party) world.set(PartyState, { ...world.get(PartyState)!, ...snap.party });
  if (snap.matchWins) restoreMatchWins(world, snap.matchWins);
  if (snap.armsRace) {
    if (!world.get(ArmsRaceState)) world.add(ArmsRaceState());
    world.set(ArmsRaceState, snap.armsRace);
  }
  const byNet = new Map<number, (typeof snap.entities)[0]>();
  for (const e of snap.entities) byNet.set(e.netId, e);
  world.query(NetId).updateEach(([net], entity) => {
    const rec = byNet.get(net.id);
    if (!rec) return;
    const streak = rec.traits.KillStreak;
    if (streak && entity.has(Player)) {
      if (!entity.has(KillStreak)) entity.add(KillStreak());
      entity.set(KillStreak, { current: Number(streak.current), best: Number(streak.best) });
    }
    const race = rec.traits.ArmsRacePlayer;
    if (race) {
      if (!entity.has(ArmsRacePlayer)) entity.add(ArmsRacePlayer());
      entity.set(ArmsRacePlayer, {
        stage: Number(race.stage),
        stageKills: Number(race.stageKills),
        totalKills: Number(race.totalKills),
      });
    }
    const lastHit = rec.traits.ArmsRaceLastHit;
    if (lastHit) {
      if (!entity.has(ArmsRaceLastHit)) entity.add(ArmsRaceLastHit());
      entity.set(ArmsRaceLastHit, {
        sourceSlot: Number(lastHit.sourceSlot),
        weaponDef: Number(lastHit.weaponDef),
        stage: Number(lastHit.stage),
        tick: Number(lastHit.tick),
      });
    }
    const partyPlayer = rec.traits.PartyPlayer;
    if (partyPlayer && entity.has(PartyPlayer))
      entity.set(PartyPlayer, { ...entity.get(PartyPlayer)!, ...partyPlayer } as NonNullable<
        ReturnType<typeof entity.get<typeof PartyPlayer>>
      >);
    const t = rec.traits.Transform;
    if (t && entity.get(Transform)) {
      entity.set(Transform, { x: Number(t.x), y: Number(t.y), angle: Number(t.angle) });
      const body = ctx.bodies.get(entity);
      body?.setPosition({ x: Number(t.x), y: Number(t.y) });
      body?.setAngle(Number(t.angle));
    }
    const h = rec.traits.Health;
    if (h && entity.get(Health)) entity.set(Health, { hp: Number(h.hp), maxHp: Number(h.maxHp) });
    const hazard = rec.traits.Hazard;
    if (hazard?.kind === HazardKind.Cannon && entity.has(Hazard))
      entity.set(Hazard, { ...entity.get(Hazard)!, ...hazard } as NonNullable<ReturnType<typeof entity.get<typeof Hazard>>>);
    const projectile = rec.traits.Projectile;
    if (projectile && entity.has(Projectile) &&
        (projectile.kind === ProjectileKind.ReturnDisc || projectile.kind === ProjectileKind.Siphon)) {
      entity.set(Projectile, { ...entity.get(Projectile)!, ...projectile } as NonNullable<ReturnType<typeof entity.get<typeof Projectile>>>);
    }
    const weapon = rec.traits.Weapon;
    if (weapon && entity.has(Weapon)) {
      entity.set(Weapon, { ...entity.get(Weapon)!, defId: Number(weapon.defId), ammo: Number(weapon.ammo), thrown: Boolean(weapon.thrown) });
    }
  });
}

export function hashWorld(world: World): string {
  const snap = serializeWorld(world);
  const nums: number[] = [snap.tick, snap.rng, snap.nextId];
  if (snap.matchWins) nums.push(...snap.matchWins);
  if (snap.armsRace) nums.push(snap.armsRace.winner);
  for (const key of Object.keys(snap.party ?? {}).sort()) {
    const value = snap.party![key];
    nums.push(typeof value === 'boolean' ? (value ? 1 : 0) : quantize(value as number));
  }
  for (const e of snap.entities) {
    nums.push(e.netId);
    for (const key of Object.keys(e.traits).sort()) {
      const rec = e.traits[key]!;
      for (const field of Object.keys(rec).sort()) {
        const v = rec[field];
        if (typeof v === 'number') nums.push(quantize(v));
        else if (typeof v === 'boolean') nums.push(v ? 1 : 0);
      }
    }
  }
  return hashToHex(fnv1a(nums));
}
